import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HelpCircle,
  Shield,
  Phone,
  Info,
  Users,
  UserCheck,
  FileText,
  Scale,
  ArrowRight,
} from "lucide-react";
import { useAppSelector } from "../../store/hooks";

const QuickLinks = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const quickLinks = [
    {
      icon: HelpCircle,
      title: "Help Center",
      description: "Find answers to common questions",
      href: "/help-center",
      color: "bg-blue-500",
    },
    {
      icon: Shield,
      title: "Safety Information",
      description: "Travel safety tips and guidelines",
      href: "/safety-information",
      color: "bg-emerald-500",
    },
    {
      icon: Phone,
      title: "Contact Us",
      description: "Get in touch with our support team",
      href: "/contact-us",
      color: "bg-purple-500",
    },
    {
      icon: Info,
      title: "About Us",
      description: "Learn more about TripSecure",
      href: "/about-us",
      color: "bg-orange-500",
    },
    {
      icon: Users,
      title: "Become a Provider",
      description: "Join our partner network",
      href: "/become-a-provider",
      color: "bg-green-500",
    },
    {
      icon: UserCheck,
      title: "Become an Agent",
      description: "Start earning as an agent",
      href: "/become-an-agent",
      color: "bg-indigo-500",
    },
    {
      icon: FileText,
      title: "Privacy Policy",
      description: "How we protect your data",
      href: "/privacy-policy",
      color: "bg-gray-500",
    },
    {
      icon: Scale,
      title: "Terms of Service",
      description: "Our terms and conditions",
      href: "/terms-of-service",
      color: "bg-red-500",
    },
  ];

  return (
    <section className="py-16 px-4 mx-auto max-w-7xl sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Quick Access</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Everything you need to know about TripSecure in one place
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {quickLinks.map((link, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            whileHover={{ y: -5, scale: 1.02 }}
            className="group"
          >
            <Link
              to={link.href}
              className="block p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 h-full"
            >
              <div
                className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${link.color} mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                <link.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                {link.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">{link.description}</p>
              <div className="flex items-center text-emerald-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                <span>Learn more</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      {!isAuthenticated && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 text-white text-center"
        >
          <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-lg mb-6 opacity-90">
            Join thousands of travelers who trust TripSecure for their safety
            and peace of mind.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/signup"
              className="px-8 py-3 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
            >
              Sign Up Now
            </Link>
            <Link
              to="/signin"
              className="px-8 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-emerald-600 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default QuickLinks;
